import { Button, Form, Modal, Spin } from "antd";
import PropTypes from "prop-types";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import apiClient from "../../../../helpers/apiClient";
import { useToast } from "../../../../contexts/ToastContext";
import { useCategoriesContext } from "../../../../contexts/CategoriesContext";
import uploadFile from "../../../../utilities/uploadFile";
import deleteFile from "../../../../utilities/deleteFile";
import useCarPricingMode from "../../hooks/useCarPricingMode";
import normalizeRate from "./categoryRateNormalize";
import CategoryFormFields from "./CategoryFormFields";

const DEFAULT_RATE_TOGGLES = {
    hourly: true,
    daily: true,
    weekly: true,
    monthly: true,
};

const RATE_FIELD_MAP = {
    hourly: "hourlyRate",
    daily: "dailyRate",
    weekly: "weeklyRate",
    monthly: "monthlyRate",
};

const CategoryFormModal = ({ open, categoryId = null, onCancel, onSuccess }) => {
    const { t } = useTranslation("cars");
    const toast = useToast();
    const { fetchCategories } = useCategoriesContext();
    const isCarPricingMode = useCarPricingMode();
    const fileInputRef = useRef(null);
    const [form] = Form.useForm();

    const isEditMode = Boolean(categoryId);

    const [loading, setLoading] = useState(false);
    const [initialLoading, setInitialLoading] = useState(false);
    const [previewUrl, setPreviewUrl] = useState("");
    const [existingImageUrl, setExistingImageUrl] = useState("");
    const [categoryStatus, setCategoryStatus] = useState("active");
    const [imageValidation, setImageValidation] = useState(false);
    const [rateToggles, setRateToggles] = useState(DEFAULT_RATE_TOGGLES);

    const resetFormState = useCallback(() => {
        form.resetFields();
        setPreviewUrl("");
        setExistingImageUrl("");
        setImageValidation(false);
        setCategoryStatus("active");
        setRateToggles(DEFAULT_RATE_TOGGLES);
        if (fileInputRef.current) fileInputRef.current.value = "";
    }, [form]);

    useEffect(() => {
        if (!open) return undefined;

        if (!isEditMode) {
            resetFormState();
            form.setFieldsValue({
                disableHourly: true,
                disableDaily: true,
                disableWeekly: true,
                disableMonthly: true,
            });
            return undefined;
        }

        let cancelled = false;

        const fetchCategoryData = async () => {
            setInitialLoading(true);
            try {
                const response = await apiClient.get(`/api/car-category-v2/list?categoryId=${categoryId}`);
                if (cancelled) return;

                if (response?.data?.success && response.data.data.length > 0) {
                    const category = response.data.data[0];
                    const hourlyRate = normalizeRate(category.hourlyRate);
                    const dailyRate = normalizeRate(category.dailyRate);
                    const weeklyRate = normalizeRate(category.weeklyRate);
                    const monthlyRate = normalizeRate(category.monthlyRate);

                    setCategoryStatus(category.status || "active");

                    if (category.image) {
                        setPreviewUrl(category.image);
                        setExistingImageUrl(category.image);
                    } else {
                        setPreviewUrl("");
                        setExistingImageUrl("");
                    }

                    setRateToggles({
                        hourly: !hourlyRate.disabled,
                        daily: !dailyRate.disabled,
                        weekly: !weeklyRate.disabled,
                        monthly: !monthlyRate.disabled,
                    });

                    form.setFieldsValue({
                        category: category.categoryName,
                        seats: category.seats,
                        bags: category.bags,
                        hourlyRate: hourlyRate.rate,
                        dailyRate: dailyRate.rate,
                        weeklyRate: weeklyRate.rate,
                        monthlyRate: monthlyRate.rate,
                        disableHourly: !hourlyRate.disabled,
                        disableDaily: !dailyRate.disabled,
                        disableWeekly: !weeklyRate.disabled,
                        disableMonthly: !monthlyRate.disabled,
                    });
                }
            } catch (error) {
                if (!cancelled) {
                    toast.error({
                        message: t("rates.failedToFetchCategory"),
                        description: error.response?.data?.message || error.message || t("rates.unknownError"),
                    });
                }
            } finally {
                if (!cancelled) setInitialLoading(false);
            }
        };

        fetchCategoryData();

        return () => {
            cancelled = true;
        };
    }, [open, isEditMode, categoryId, form, toast, t, resetFormState]);

    const handleClose = () => {
        resetFormState();
        onCancel?.();
    };

    const handleFileChange = (e) => {
        if (e.target.files[0]) {
            setPreviewUrl(URL.createObjectURL(e.target.files[0]));
            setImageValidation(false);
        }
    };

    const handleClearImage = () => {
        if (fileInputRef.current) fileInputRef.current.value = "";
        setPreviewUrl("");
        setImageValidation(true);
    };

    const handleRateToggle = (key, checked) => {
        setRateToggles((prev) => ({ ...prev, [key]: checked }));
        if (!checked) {
            form.setFieldValue(RATE_FIELD_MAP[key], null);
        }
    };

    const buildApiPayload = (values, imageUrl) => {
        if (isCarPricingMode) {
            return {
                categoryName: values.category,
                image: imageUrl,
                status: isEditMode ? categoryStatus : "active",
            };
        }

        return {
            categoryName: values.category,
            image: imageUrl,
            seats: values.seats,
            bags: values.bags,
            hourlyRate: { disabled: !rateToggles.hourly, rate: values.hourlyRate || 0 },
            dailyRate: { disabled: !rateToggles.daily, rate: values.dailyRate || 0 },
            weeklyRate: { disabled: !rateToggles.weekly, rate: values.weeklyRate || 0 },
            monthlyRate: { disabled: !rateToggles.monthly, rate: values.monthlyRate || 0 },
            status: isEditMode ? categoryStatus : "active",
        };
    };

    const onFinish = async (values) => {
        const hasNewFile = !!fileInputRef.current?.files[0];
        const hasExistingImage = !!previewUrl;

        if (!hasNewFile && !hasExistingImage) {
            setImageValidation(true);
            return;
        }

        setLoading(true);
        try {
            setImageValidation(false);

            let imageUrl = previewUrl;
            if (hasNewFile) {
                imageUrl = await uploadFile(fileInputRef.current.files[0]);
                if (isEditMode && existingImageUrl && existingImageUrl !== imageUrl) {
                    await deleteFile(existingImageUrl);
                }
            }

            const apiData = buildApiPayload(values, imageUrl);
            const response = isEditMode
                ? await apiClient.patch(`/api/car-category-v2/${categoryId}`, apiData)
                : await apiClient.post("/api/car-category-v2/create", apiData);

            if (response?.data?.success) {
                toast.success({
                    message: isEditMode ? t("rates.categoryUpdated") : t("rates.categoryCreated"),
                });
                await fetchCategories();
                onSuccess?.();
                handleClose();
            }
        } catch (error) {
            toast.error({
                message: isEditMode ? t("rates.failedToUpdateCategory") : t("rates.failedToCreateCategory"),
                description: error.response?.data?.message || error.message || t("rates.unknownError"),
            });
        } finally {
            setLoading(false);
        }
    };

    const modalTitle = isEditMode ? t("editCategoriesAndRates") : t("rates.createCategoriesAndRates");
    const submitLabel = isEditMode
        ? loading
            ? t("rates.updating")
            : t("rates.update")
        : loading
          ? t("rates.saving")
          : t("rates.save");

    return (
        <Modal
            open={open}
            onCancel={handleClose}
            title={
                <div>
                    <p className="text-lg font-semibold text-gray-900">{modalTitle}</p>
                    {isCarPricingMode ? (
                        <p className="mt-0.5 text-sm font-normal text-gray-500">{t("rates.labelModeNote")}</p>
                    ) : null}
                </div>
            }
            width={isCarPricingMode ? 520 : 860}
            centered
            destroyOnClose
            footer={
                <div className="flex justify-end gap-2">
                    <Button shape="round" onClick={handleClose}>
                        {t("rates.cancel")}
                    </Button>
                    <Button
                        type="primary"
                        shape="round"
                        onClick={() => form.submit()}
                        loading={loading}
                        className="border-none bg-[#5d5dff] hover:bg-[#4e4ee8]">
                        {submitLabel}
                    </Button>
                </div>
            }
            styles={{
                body: { maxHeight: "min(72vh, 720px)", overflowY: "auto", paddingTop: 8 },
            }}>
            {initialLoading ? (
                <div className="flex h-48 items-center justify-center">
                    <Spin size="large" />
                </div>
            ) : (
                <CategoryFormFields
                    form={form}
                    onFinish={onFinish}
                    isCarPricingMode={isCarPricingMode}
                    previewUrl={previewUrl}
                    imageValidation={imageValidation}
                    fileInputRef={fileInputRef}
                    onFileChange={handleFileChange}
                    onClearImage={handleClearImage}
                    rateToggles={rateToggles}
                    onRateToggle={handleRateToggle}
                />
            )}
        </Modal>
    );
};

CategoryFormModal.propTypes = {
    open: PropTypes.bool.isRequired,
    categoryId: PropTypes.string,
    onCancel: PropTypes.func.isRequired,
    onSuccess: PropTypes.func,
};

export default CategoryFormModal;
